import type { JSONValue } from '@st/util/json-value'
import { Context, Effect, Layer } from 'effect'

export type JsonStoreService = {
  read: (key: string) => Effect.Effect<JSONValue | undefined>
  write: (key: string, value: Exclude<JSONValue, undefined>) => Effect.Effect<void>
  remove: (key: string) => Effect.Effect<void>
}

function makeLocalStorageStore(): JsonStoreService {
  return {
    read: (key) =>
      Effect.sync(() => {
        if (typeof localStorage == 'undefined') return undefined
        const value = localStorage.getItem(key)
        return value == null ? undefined : (JSON.parse(value) as JSONValue)
      }),
    write: (key, value) =>
      Effect.sync(() => {
        if (typeof localStorage == 'undefined') return
        localStorage.setItem(key, JSON.stringify(value))
      }),
    remove: (key) =>
      Effect.sync(() => {
        if (typeof localStorage == 'undefined') return
        localStorage.removeItem(key)
      })
  }
}

function makeMemoryStore(): JsonStoreService {
  const data = new Map<string, string>()

  return {
    read: (key) =>
      Effect.sync(() => {
        const value = data.get(key)
        return value == null ? undefined : (JSON.parse(value) as JSONValue)
      }),
    write: (key, value) =>
      Effect.sync(() => {
        data.set(key, JSON.stringify(value))
      }),
    remove: (key) =>
      Effect.sync(() => {
        data.delete(key)
      })
  }
}

export class JsonStore extends Context.Service<JsonStore, JsonStoreService>()('JsonStore') {
  static LocalStorage = Layer.succeed(this, makeLocalStorageStore())

  static Memory = Layer.sync(this, makeMemoryStore)
}

export class SessionJsonStore extends Context.Service<SessionJsonStore, JsonStoreService>()(
  'SessionJsonStore'
) {
  static LocalStorage = Layer.succeed(this, makeLocalStorageStore())

  static Memory = Layer.sync(this, makeMemoryStore)
}
